import React, { useMemo, useState } from "react";
import { useRouter } from "expo-router";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useAtomValue, useSetAtom } from "jotai/react";
import { useForm } from "react-hook-form";
import z from "zod";

import { ApplicationScreenLayout } from "@/blocks/ApplicationScreenLayout";
import { VnBox } from "@/components/VnBox";
import { VnDateOfBirthInput } from "@/components/VnDateOfBirthInput";
import { VnExpandableCard } from "@/components/VnExpandableCard";
import { VnForm } from "@/components/VnForm";
import { useUpdateHouseholdMemberMutation } from "@/data/mutations/householdMembersMutations";
import { useUpdateCurrentPatientMutation } from "@/data/mutations/patientsMutations";
import { useListHouseholdMembersQuery } from "@/data/queries/householdMembersQueries";
import {
  dateOfBirthSchema,
  HouseholdMemberType,
} from "@/domain/householdMember";
import {
  applicationCurrentStepAtom,
  applicationTotalStepsAtom,
} from "@/stores/atoms";
import { dateAsDayjs } from "@/utils/date";
import { formatHouseholdMemberName } from "@/utils/householdMember";

type MemberDateOfBirthFormPropsType = {
  member: HouseholdMemberType;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onClose: () => void;
};

type MembersDateOfBirthFormType = Pick<HouseholdMemberType, "dateOfBirth">;

const membersDateOfBirthFormSchema = z.object({
  dateOfBirth: dateOfBirthSchema,
});

const MemberDateOfBirthForm: React.FC<MemberDateOfBirthFormPropsType> = ({
  member,
  isExpanded,
  onToggleExpand,
  onClose,
}) => {
  const dateOfBirth = member.dateOfBirth ?? null;
  const form = useForm<MembersDateOfBirthFormType>({
    defaultValues: {
      dateOfBirth: dateOfBirth ?? null,
    },
    resolver: standardSchemaResolver(membersDateOfBirthFormSchema),
  });
  const [shouldShowEditButton, setShouldShowEditButton] = useState(
    Boolean(dateOfBirth),
  );

  const { mutateAsync: updateHouseholdMember, isPending } =
    useUpdateHouseholdMemberMutation();
  const handleSave = (formData: MembersDateOfBirthFormType) => {
    void updateHouseholdMember(
      {
        id: member.id,
        householdMemberData: {
          dateOfBirth: formData.dateOfBirth,
        },
      },
      {
        onSuccess: () => {
          setShouldShowEditButton(true);
          onToggleExpand();
        },
      },
    );
  };
  const onCancel = () => {
    form.reset({
      dateOfBirth: dateOfBirth ?? null,
    });
    onClose();
  };

  const onSave = () => {
    void form.handleSubmit(handleSave)();
  };

  const formatDateOfBirth = (dateString: string | null | undefined) => {
    if (!dateString) return "";
    return dateAsDayjs(dateString).format("MM/DD/YYYY");
  };

  return (
    <VnForm form={form} isInputOnlyForm>
      <VnExpandableCard
        title={formatHouseholdMemberName(member)}
        subtitle={formatDateOfBirth(dateOfBirth)}
        isExpanded={isExpanded}
        onToggleExpand={onToggleExpand}
        onSave={onSave}
        onCancel={onCancel}
        isLoading={isPending}
        shouldShowEditButton={shouldShowEditButton}>
        <VnDateOfBirthInput name="dateOfBirth" control={form.control} />
      </VnExpandableCard>
    </VnForm>
  );
};

export const ApplicationDateOfBirthScreen = () => {
  const router = useRouter();
  const setApplicationCurrentStep = useSetAtom(applicationCurrentStepAtom);
  const applicationTotalSteps = useAtomValue(applicationTotalStepsAtom);
  const { mutate: updateCurrentPatient } = useUpdateCurrentPatientMutation();

  const { data: householdMembers } = useListHouseholdMembersQuery();

  const [expandedMemberId, setExpandedMemberId] = useState<string | null>(null);

  const handleToggleExpand = (memberId: string) => {
    setExpandedMemberId(prev => (prev === memberId ? null : memberId));
  };

  const handleClose = () => {
    setExpandedMemberId(null);
  };

  const isNextDisabled = useMemo(() => {
    if (!householdMembers) return true;
    return householdMembers.some(member => {
      return !member.dateOfBirth;
    });
  }, [householdMembers]);

  const handleNext = () => {
    if (isNextDisabled) {
      return;
    }

    updateCurrentPatient(
      {
        patientData: {
          applicationLastStep: "DATE_OF_BIRTH",
        },
      },
      {
        onSuccess: () => {
          setApplicationCurrentStep(step =>
            Math.min(step + 1, applicationTotalSteps),
          );

          router.navigate({
            pathname: "/(authed)/(modal)/application/address",
          });
        },
      },
    );
  };

  return (
    <ApplicationScreenLayout
      onNextButtonPress={handleNext}
      isNextButtonDisabled={isNextDisabled}
      titleI18nKey="common:dateOfBirth"
      descriptionI18nKey="native:applicationDateOfBirthScreen.description">
      <VnBox className="gap-4">
        {householdMembers?.map(member => {
          const isExpanded = expandedMemberId === member.id;
          return (
            <MemberDateOfBirthForm
              key={member.id}
              member={member}
              isExpanded={isExpanded}
              onToggleExpand={() => handleToggleExpand(member.id)}
              onClose={handleClose}
            />
          );
        })}
      </VnBox>
    </ApplicationScreenLayout>
  );
};
